import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Divider,
  useTheme as useMuiTheme,
  CircularProgress,
} from '@mui/material';
import {
  Check,
  CheckCircle,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import { Plan } from '../services/companyService';
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';
import { getZohoSubscriptionId, getMySubscriptionData } from '../helpers/storageUtils';

interface PlansCardProps {
  plans: Plan[];
  selectedPlan?: string;
  currentPlanId?: string;
  /** When provided, "Current Plan" badge uses planCode (preferred over currentPlanId for consistency) */
  currentPlanCode?: string;
  /** Matches catalog plan.name when code and id are unavailable (trimmed, case-insensitive) */
  currentPlanName?: string;
  onPlanSelect?: (planId: string) => void;
  showCurrentPlanBadge?: boolean;
  showSelection?: boolean;
  showActionButtons?: boolean;
  gridColumns?: { xs: number; md: number; lg: number };
  subscriptionId?: string; // Optional subscription ID for cancel subscription
  platformName?: string; // Optional platform name (defaults to 'prism7')
  onCancelSuccess?: () => void; // Optional callback when cancellation succeeds
  /** Called after reactivate API succeeds (e.g. refresh subscription + close modal); may be async */
  onReactivateSuccess?: () => void | Promise<void>;
  /** Billing cycles sent to Zoho reactivate endpoint (default 1) */
  billingCycles?: number;
  /** Increment after mySubscription is persisted so this card re-reads localStorage (e.g. onboarding) */
  subscriptionSyncVersion?: number;
}

const PlansCard: React.FC<PlansCardProps> = ({
  plans,
  selectedPlan,
  currentPlanId,
  currentPlanCode,
  currentPlanName,
  onPlanSelect,
  showCurrentPlanBadge = false,
  showSelection = false,
  showActionButtons = true,
  gridColumns = { xs: 1, md: 2, lg: 3 },
  subscriptionId,
  platformName = 'prism7',
  onCancelSuccess,
  onReactivateSuccess,
  billingCycles = 1,
  subscriptionSyncVersion = 0,
}) => {
  const { isDarkMode } = useTheme();
  const muiTheme = useMuiTheme(); // Get MUI theme for primary color
  const { showSuccess, showError } = useToast();
  const [isCancelling, setIsCancelling] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false); // Loading state for reactivate POST

  // Resolve subscription ID: use prop first, then fallback to localStorage (mySubscriptionData.zohoSubscriptionId)
  const effectiveSubscriptionId = subscriptionId ?? (getZohoSubscriptionId() || undefined);

  const sub = getMySubscriptionData();
  const rawStatus =
    sub?.subscriptionStatus ??
    (sub as { subscription_status?: string } | null)?.subscription_status;
  const normStatus = typeof rawStatus === 'string' ? rawStatus.trim().toLowerCase() : '';
  // Cancelled (access until endDate): show Reactivate; any other status shows Cancel — refreshed after parent bumps subscriptionSyncVersion
  const isSubscriptionInactive = normStatus === 'inactive';

  // Handle plan click for upgrade/selection
  const handlePlanClick = (planId: string) => {
    if (onPlanSelect) {
      onPlanSelect(planId);
    }
  };

  // Handle cancel subscription
  const handleCancelSubscription = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    
    // Check if subscriptionId is available (from prop or localStorage)
    if (!effectiveSubscriptionId) {
      showError('Subscription ID is not available. Please contact support.');
      return;
    }

    // Confirm cancellation with user
    const confirmed = window.confirm(
      'Are you sure you want to cancel your subscription? This action cannot be undone.'
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setIsCancelling(true);
      
      // Get authentication token
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      // Prepare request payload
      const requestPayload = {
        subscriptionId: effectiveSubscriptionId,
        cancelAtEnd: true,
        platform_name: platformName,
      };

      // Call cancel subscription API
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.PAYMENT.ZOHO.CANCEL_SUBSCRIPTION}`,
        {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(requestPayload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.detail || `Failed to cancel subscription: ${response.status}`;
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      
      // Show success message
      if (responseData.success) {
        showSuccess(responseData.message || 'Subscription cancelled successfully');
        
        // Call optional success callback
        if (onCancelSuccess) {
          onCancelSuccess();
        }
      } else {
        throw new Error(responseData.message || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Cancel subscription error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to cancel subscription. Please try again.';
      showError(errorMessage);
    } finally {
      setIsCancelling(false);
    }
  };

  /** POST to Zoho reactivate when user restores a cancelled (inactive) subscription */
  const handleReactivateSubscription = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Keep card click from firing upgrade selection

    if (!effectiveSubscriptionId) {
      showError('Subscription ID is not available. Please contact support.');
      return;
    }

    try {
      setIsReactivating(true);
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const requestPayload = {
        subscriptionId: effectiveSubscriptionId,
        billingCycles,
        platform_name: platformName,
      };

      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.PAYMENT.ZOHO.REACTIVATE_SUBSCRIPTION}`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestPayload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.message ||
          errorData.detail ||
          `Failed to reactivate subscription: ${response.status}`;
        throw new Error(errorMessage);
      }

      const responseData = await response.json().catch(() => ({}));
      if (responseData.success === false) {
        throw new Error(responseData.message || 'Failed to reactivate subscription');
      }

      showSuccess(responseData.message || 'Subscription reactivated successfully');
      await onReactivateSuccess?.();
    } catch (error) {
      console.error('Reactivate subscription error:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to reactivate subscription. Please try again.';
      showError(errorMessage);
    } finally {
      setIsReactivating(false);
    }
  };

  return (
    <Box sx={{ 
      display: 'grid', 
      gridTemplateColumns: { 
        xs: '1fr', 
        md: `repeat(${gridColumns.md}, 1fr)`, 
        lg: `repeat(${gridColumns.lg}, 1fr)` 
      }, 
      gap: 3 
    }}>
      {plans.filter(plan => plan.isActive).sort((a, b) => {
        const priceA = Number(a.monthlyPrice) || 0;
        const priceB = Number(b.monthlyPrice) || 0;
        return priceA - priceB;
      }).map((plan) => {
        // Prefer planCode, then id, then display name (Admin Hub uses name→id; restriction modal passes name as fallback)
        const normalizedName = (s: string) => s.trim().toLowerCase();
        const isCurrentPlanByName =
          currentPlanName != null &&
          currentPlanName !== '' &&
          Boolean(plan.name) &&
          normalizedName(currentPlanName) === normalizedName(plan.name);
        const isCurrentPlan =
          (currentPlanCode && plan.planCode && currentPlanCode === plan.planCode) ||
          (currentPlanId && currentPlanId === plan.id) ||
          isCurrentPlanByName;
        const isSelected = selectedPlan === plan.id;
        
        return (
          <Box key={`${plan.id}-${subscriptionSyncVersion}`}>
            <Card 
              sx={{ 
                height: '100%',
                cursor: onPlanSelect ? 'pointer' : 'default',
                border: isCurrentPlan ? 2 : 1, // Thicker border for current plan
                borderColor: isCurrentPlan 
                  ? muiTheme.palette.primary.main // Light blue border for current plan
                  : isSelected 
                  ? 'primary.main' 
                  : 'divider',
                backgroundColor: isSelected 
                  ? (isDarkMode ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.04)')
                  : 'background.paper',
                '&:hover': onPlanSelect ? {
                  borderColor: 'primary.main',
                  backgroundColor: isDarkMode ? 'rgba(25, 118, 210, 0.04)' : 'rgba(25, 118, 210, 0.02)',
                } : {},
              }}
              onClick={() => handlePlanClick(plan.id)}
            >
              <CardContent sx={{ 
                p: 3, 
                display: 'flex', 
                flexDirection: 'column', 
                height: '100%' 
              }}>
                {/* Plan Name and Current Plan Badge */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold' }}>
                    {plan.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {showCurrentPlanBadge && isCurrentPlan && (
                      <Chip 
                        label="Current Plan" 
                        size="small" 
                        sx={{
                          backgroundColor: muiTheme.palette.primary.main, // Theme-based blue background
                          color: '#ffffff', // White text
                          fontWeight: 500,
                        }}
                      />
                    )}
                    {showSelection && isSelected && (
                      <CheckCircle color="primary" />
                    )}
                  </Box>
                </Box>
                
                {/* Plan Description (max 2 lines) */}
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 2,
                    display: '-webkit-box',
                    WebkitLineClamp: 2, // Limit to 2 lines
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {plan.description ? plan.description.charAt(0).toUpperCase() + plan.description.slice(1) : plan.description}
                </Typography>
                
                {/* Plan Price */}
                <Typography variant="h4" component="div" sx={{ mb: 2, fontWeight: 'bold' }}>
                  {Number(plan.monthlyPrice) === 0 ? (
                    'Free'
                  ) : (
                    <>
                      ${Number(plan.monthlyPrice)}
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                        / month
                      </Typography>
                    </>
                  )}
                </Typography>
                
                {/* One line border separator */}
                <Divider sx={{ my: 2 }} />
                
                {/* Features Section - This will grow to fill available space */}
                {plan.features && plan.features.length > 0 && (
                  <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography 
                      variant="subtitle2" 
                      component="div" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 1.5,
                        color: 'text.primary',
                      }}
                    >
                      Features
                    </Typography>
                    
                    {/* List of Features */}
                    <Box>
                      {plan.features.map((feature: string, index: number) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                          <Check sx={{ fontSize: 16, mr: 1, color: 'text.primary', mt: 0.25, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ color: 'text.primary' }}>
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
                
                {/* Action Button - Positioned at the bottom using margin-top auto */}
                {showActionButtons && (
                  <Box sx={{ mt: 'auto', pt: 3, display: 'flex', justifyContent: 'center' }}>
                    {isCurrentPlan ? (
                      // When subscription is inactive (cancelled), show Reactivate; otherwise show Cancel
                      isSubscriptionInactive ? (
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={handleReactivateSubscription}
                          disabled={isReactivating}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            py: 1.5,
                            backgroundColor: '#ed6c02', // Match SubscriptionCancellationBanner
                            color: '#fff',
                            '&:hover': {
                              backgroundColor: '#e65100',
                            },
                          }}
                        >
                          {isReactivating ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={16} sx={{ color: '#fff' }} />
                              Reactivating...
                            </Box>
                          ) : (
                            'Reactivate Subscription'
                          )}
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={handleCancelSubscription}
                          disabled={isCancelling}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            py: 1.5,
                            borderColor: 'error.main', // Red border
                            color: 'error.main', // Red text
                            '&:hover': {
                              borderColor: 'error.dark',
                              backgroundColor: 'error.light',
                              color: '#ffffff',
                            },
                            '&:disabled': {
                              borderColor: 'error.main',
                              color: 'error.main',
                              opacity: 0.6,
                            },
                          }}
                        >
                          {isCancelling ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={16} sx={{ color: 'error.main' }} />
                              Cancelling...
                            </Box>
                          ) : (
                            'Cancel Subscription'
                          )}
                        </Button>
                      )
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card click
                          onPlanSelect?.(plan.id);
                        }}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 500,
                          py: 1.5,
                        }}
                      >
                        Upgrade to this plan
                      </Button>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        );
      })}
    </Box>
  );
};

export default PlansCard;
